
import React from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BioQualityIndicator } from '@/components/shared/BioQualityIndicator';
import { BioProgressIndicator } from '@/components/shared/BioProgressIndicator';
import { BioGuidance } from '@/components/shared/BioGuidance';
import { BioValidationResult } from '@/hooks/useBioAIValidation';
import { AlertCircle, CheckCircle2, Brain, Loader2, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BIO_MIN_LENGTH, BIO_MAX_LENGTH } from '@/core/BusinessRulesEngine';

interface BioInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (canSave: boolean, canProceed: boolean, validationData?: any) => void;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  showQualityIndicator?: boolean;
  showGuidance?: boolean;
  showValidationIcon?: boolean;
  className?: string;
  disabled?: boolean;
  occupation?: string;
  education?: string;
  // External validation props - when provided, disables internal validation
  externalValidation?: BioValidationResult;
}

export const BioInput: React.FC<BioInputProps> = ({
  value,
  onChange,
  onValidationChange,
  label = "Biografia",
  placeholder = "Conte um pouco sobre você, seus interesses e o que está buscando...",
  maxLength = BIO_MAX_LENGTH,
  showQualityIndicator = true,
  showGuidance = true,
  showValidationIcon = true,
  className = "",
  disabled = false,
  occupation,
  education,
  externalValidation
}) => {
  // Use external validation if provided, otherwise create default validation result
  const aiValidation = externalValidation || {
    isWellWritten: false,
    topicsMentioned: [],
    feedback: '',
    score: 0,
    loading: false,
    canSave: false,
    canProceed: false,
    shouldBlock: true,
    hasBeenValidated: false
  };
  const [internalShowGuidance, setInternalShowGuidance] = React.useState(false);

  const minLength = BIO_MIN_LENGTH;

  // Notify parent component about validation changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(aiValidation.canSave, aiValidation.canProceed, aiValidation);
    }
  }, [aiValidation.canSave, aiValidation.canProceed, aiValidation, onValidationChange]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const getValidationIcon = () => {
    if (aiValidation.loading) {
      return <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />;
    }
    // Only show validation icons after AI has validated
    if (value && aiValidation.hasBeenValidated) {
      if (aiValidation.isWellWritten) {
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      } else {
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      }
    }
    return null;
  };

  // Simplified logic: only show guidance when manually requested or when bio is short
  const showTips = (() => {
    // If showGuidance prop is false, never show guidance
    if (!showGuidance) return false;
    
    // If user manually requested guidance, always show
    if (internalShowGuidance) return true;
    
    // Never show tips automatically since we removed auto AI validation
    return false;
  })();

  return (
    <TooltipProvider>
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="bio" className="text-white font-medium">
              {label}
            </Label>
            {showValidationIcon && getValidationIcon()}
            {showGuidance && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-white/50 hover:text-white/70"
                    onClick={() => setInternalShowGuidance(!internalShowGuidance)}
                  >
                    <HelpCircle className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Clique para ver dicas de como escrever uma boa biografia</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Brain className="h-3 w-3 text-blue-400" />
              <span className="text-xs text-blue-400">Inteligência Artificial</span>
            </div>
          </div>
        </div>
        
        {/* Guidance card - now properly hidden when AI approves */}
        <BioGuidance 
          occupation={occupation}
          education={education}
          showTips={showTips}
        />
        
        {/* Progress indicator - show only basic character progress */}
        <BioProgressIndicator
          bioLength={value.length}
          minLength={minLength}
          isAiValidating={false}
          isAiValid={false}
          hasAiValidated={false}
        />
        
        {/* Textarea */}
        <div className="relative">
          <Textarea 
            id="bio" 
            value={value} 
            onChange={handleChange} 
            placeholder={placeholder} 
            rows={Math.max(10, Math.ceil(value.length / 60) + 3)}
            disabled={disabled} 
            className={`bg-black/30 border border-white/20 text-white placeholder:text-white/50 resize-none focus:border-luxury-gold transition-colors ${
              aiValidation.error && value && aiValidation.hasBeenValidated ? 'border-red-500 focus:border-red-500' : ''
            }`} 
            maxLength={maxLength} 
          />
        </div>
        
        {/* Quality indicator - don't show since we removed automatic AI validation */}
        {showQualityIndicator && value.length >= minLength && externalValidation && (
          <BioQualityIndicator 
            bio={value} 
            className="mt-3" 
            aiEvaluation={externalValidation} 
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default BioInput;
